import React, { useEffect, useState } from "react";
import {
  Bell,
  CircleUser,
  LogOut,
  ExternalLink,
  Menu,
  Mail,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Images from "../Images";
import MobileSidebar from "./MobileSidebar";
import { logoutUser } from "../requests/auth/Authentication";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../redux/store";

const Navbar = ({page}) => {
  const user = useSelector((state) => state?.user?.user) || null;
  const subscription = user?.subscription || null;

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const isSubscriptionPage = () => {
    const subscriptionPages = ["/subscription", "/pricing", "/payment"];
    return subscriptionPages.includes(location.pathname);
  };
  const [remainingDays, setRemainingDays] = useState(0);

  const calculateDaysLeft = () => {
    const currentDate = new Date();
    const subscriptionEndDate = new Date(subscription.end_date.replace(' ', 'T'));

    if (isNaN(subscriptionEndDate.getTime())) {
      console.error('Invalid subscription end date');
      setRemainingDays(0);
      return;
    }
    const timeDifference = subscriptionEndDate - currentDate;
    const daysDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
    
    // If subscription has expired (negative days), set to 0
    setRemainingDays(Math.max(0, daysDifference));
  };

  useEffect(() => {
    if (subscription && subscription.end_date) {
      calculateDaysLeft();
    } else {
      setRemainingDays(0);
    }
  }, [subscription]);

  const handleProfileClick = () => setAccountOpen(false);

  const handleAccount = () => {
    setAccountOpen(!accountOpen);
    setNotificationsOpen(false);
  };

  const handleNotifications = () => {
    setNotificationsOpen(!notificationsOpen);
    setAccountOpen(false);
  };

  const handleLogout = async () => {
    setLoading(true);
    try {
      await logoutUser();
      dispatch(logout());
      // The logout function in Redux store will handle navigation to landing page
    } catch (error) {
      console.error("Logout failed:", error);
      // If logout fails, still navigate to landing page
      window.location.href = "/";
    } finally {
      setLoading(false);
    }
  };

  const dropdownVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };

  const isMobileView = () => {
    return window.innerWidth < 768;
  };

  const renderNotificationsDropdown = () => {
    if (!notificationsOpen) return null;

    const mobileStyles = isMobileView()
      ? "fixed left-4 right-4 top-[72px]"
      : "absolute right-0 w-80";

    return (
      <motion.div
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={dropdownVariants}
        className={`bg-white rounded-lg border shadow-lg ${mobileStyles}`}
      >
        {/* <div className="p-4 bg-gradient-to-r from-[#FF6C6C] to-pink-500">
          <h2 className="text-lg font-semibold text-white">Notifications</h2>
        </div> */}

        {/* <div className="max-h-[350px] overflow-y-auto">
          <div className="p-3 border-b transition duration-200 cursor-pointer hover:bg-gray-50">
            <div className="flex gap-3 items-start">
              <div className="mt-2 w-2 h-2 bg-blue-500 rounded-full"></div>
              <div>
                <p className="text-sm font-medium text-gray-800">
                  Your have just redeemed order #123! 🎉
                </p>
                <p className="mt-1 text-xs text-gray-500">2 minutes ago</p>
              </div>
            </div>
          </div>

          <div className="p-3 border-b transition duration-200 cursor-pointer hover:bg-gray-50">
            <div className="flex gap-3 items-start">
              <div className="mt-2 w-2 h-2 bg-green-500 rounded-full"></div>
              <div>
                <p className="text-sm font-medium text-gray-800">
                  Special offer: Get 20% off on Electronics!
                </p>
                <p className="mt-1 text-xs text-gray-500">1 hour ago</p>
              </div>
            </div>
          </div>

          <div className="p-3 border-b transition duration-200 cursor-pointer hover:bg-gray-50">
            <div className="flex gap-3 items-start">
              <div className="mt-2 w-2 h-2 bg-purple-500 rounded-full"></div>
              <div>
                <p className="text-sm font-medium text-gray-800">
                  New restaurant joined near you: "The Tasty Corner"
                </p>
                <p className="mt-1 text-xs text-gray-500">2 hours ago</p>
              </div>
            </div>
          </div>
        </div> */}

        {/* <Link
          to="/notification"
          className="flex items-center justify-center gap-2 p-3 text-[#FF6C6C] hover:bg-pink-50 transition duration-200"
          onClick={() => {
            setNotificationsOpen(false);
            navigate("/notification");
          }}
        >
          View All Notifications
          <ExternalLink size={16} />
        </Link> */}
      </motion.div>
    );
  };

  const DEFAULT_AVATAR = "https://api.dicebear.com/7.x/avataaars/svg?seed=Robert&backgroundColor=FF6C6C";

  const isFreeTrial = subscription?.subscription_plan?.name === "Free Trial";
  const hasLowDaysRemaining = remainingDays < 10;

  const navigateToSubscription = () => {
    navigate("/pricing");
  }

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (accountOpen && !event.target.closest('.account-dropdown')) {
        setAccountOpen(false);
      }
      if (notificationsOpen && !event.target.closest('.notifications-dropdown')) {
        setNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [accountOpen, notificationsOpen]);

  return (
    <>
      <div className="sticky top-0 z-40 flex items-center justify-between w-full bg-white shadow-sm h-16 px-4 md:px-8">
        {/* Left Section */}
        <div className="flex gap-3 items-center min-w-0">
          <button
            onClick={() => setMobileSidebarOpen(true)}
            className="p-1.5 text-gray-700 hover:text-gray-900 md:hidden"
          >
            <Menu size={22} />
          </button>
          <img
            src={Images.logo}
            alt="Dealboxx - Logo"
            className="h-7 md:hidden"
          />
           <div className="hidden gap-2.5 items-center md:flex min-w-0">
            <div className="h-6 w-[2px] bg-gradient-to-b from-[#FF6C6C] to-[#FF9B9B] flex-shrink-0"></div>
            <div className="flex flex-col gap-0.5 min-w-0">
              <motion.h1 
                className="text-sm font-semibold text-gray-800 truncate leading-tight"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                {page || 'Dashboard'}
              </motion.h1>
              <motion.div 
                className="h-[2px] w-10 bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B]"
                initial={{ width: 0 }}
                animate={{ width: 40 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              />
            </div>
          </div>
        </div>

        

        {/* Desktop middle section - Subscription badge commented out */}
        {/* {!isSubscriptionPage() && (isFreeTrial || hasLowDaysRemaining) && (
        <div className="hidden gap-4 items-center md:flex">
          <div className="relative">
            <div className="mx-auto max-w-3xl">
              <div className="flex justify-between items-center p-3 bg-white rounded-md border-l-8 border-orange-500 shadow-md">
                <div className="flex justify-center items-center space-x-2">
                  <svg
                    className="w-5 h-5 text-orange-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 2a4 4 0 00-4 4v2H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-1V6a4 4 0 00-4-4zm-2 6V6a2 2 0 114 0v2H8z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="flex gap-3 items-center">
                    <span className="text-[11px] text-gray-700 max-w-[250px]">
                      {isFreeTrial ? (
                        remainingDays === 0 ? (
                          <>Your free trial has <span className="font-semibold text-orange-500">expired</span></>
                        ) : (
                          <>Your free trial ends in <span className="font-semibold text-orange-500">{remainingDays} days</span></>
                        )
                      ) : (
                        remainingDays === 0 ? (
                          <>Your subscription has <span className="font-semibold text-orange-500">expired</span></>
                        ) : (
                          <>Your subscription ends in <span className="font-semibold text-orange-500">{remainingDays} days</span></>
                        )
                      )}
                      . Upgrade to dealboxx basic or professional plan
                    </span>
                  </div>
                  <button onClick={navigateToSubscription} className="px-3 py-2 text-xs font-medium text-orange-500 bg-white rounded border border-orange-500 transition duration-300 ease-in-out hover:bg-orange-600 hover:text-white">
                    Upgrade Plan
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        )} */}
        

        {/* Right Section */}
        <div className="flex gap-3 items-center flex-shrink-0">
          <div className="hidden gap-4 items-center md:flex">
            <div className="mx-auto max-w-md">
              {/* <div className="hidden relative md:flex">
                <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                  <svg
                    className="w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <input
                  type="search"
                  className="w-full py-2 pl-10 pr-4 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-[#FF6C6C] focus:ring-1 focus:ring-[#FF6C6C]"
                  placeholder="Search..."
                />
              </div> */}
            </div>

            {/* Notifications */}
            {/* <div className="relative">
              <button onClick={handleNotifications} className="relative">
                <Bell size={24} className="text-gray-700" />
                <span className="flex absolute -top-1 -right-1 justify-center items-center w-5 h-5 text-xs text-white bg-red-500 rounded-full">
                  3
                </span>
              </button>
              {notificationsOpen && renderNotificationsDropdown()}
            </div> */}

            <div className="relative account-dropdown">
              <button
                onClick={handleAccount}
                className={`transition-colors duration-200 ${
                  accountOpen
                    ? "ring-2 ring-[#FF6C6C] rounded-full"
                    : "hover:ring-2 hover:ring-gray-200 rounded-full"
                }`}
              >
                <img
                  src={user.avatar || DEFAULT_AVATAR}
                  alt={user.brand_name}
                  className="w-9 h-9 rounded-full"
                />
              </button>
              <AnimatePresence>
                {accountOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 mt-2 w-72 bg-white rounded-lg border shadow-lg"
                  >
                    <div className="p-4 bg-gradient-to-r from-[#FF6C6C] to-pink-500">
                      <div className="flex gap-3 items-center">
                        <img
                          src={user.avatar || DEFAULT_AVATAR}
                          alt={user.brand_name}
                          className="w-9 h-9 rounded-full border-2 border-white"
                        />
                        <div className="text-white">
                          <h3 className="font-semibold">{user.brand_name}</h3>
                          <div className="flex gap-1 items-center text-xs">
                            <Mail size={12} />
                            <span>{user.email}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Menu Items */}
                    <ul className="py-2">
                      <li>
                        <Link
                          to="/profile-settings"
                          className="flex items-center gap-2 px-4 py-2 text-gray-800 hover:bg-[#FF6C6C] hover:text-white"
                          onClick={handleProfileClick}
                        >
                          <CircleUser size={20} />
                          <span>My Profile</span>
                        </Link>
                      </li>
                      <li>
                        <button
                          onClick={handleLogout}
                          className="flex w-full items-center gap-2 px-4 py-2 text-gray-800 hover:bg-[#FF6C6C] hover:text-white"
                        >
                          <LogOut size={20} />
                          <span>Logout</span>
                        </button>
                      </li>
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Mobile Icons */}
          <div className="flex gap-3 items-center md:hidden">
            <div className="relative">
              <button onClick={handleNotifications}>
                <Bell size={22} className="text-gray-700" />
                <span className="flex absolute -top-1 -right-1 justify-center items-center w-4 h-4 text-[10px] text-white bg-red-500 rounded-full">
                  3
                </span>
              </button>
              {notificationsOpen && renderNotificationsDropdown()}
            </div>

            <div className="relative">
              <button
                onClick={handleAccount}
                className={`transition-colors duration-200 ${
                  accountOpen
                    ? "ring-2 ring-[#FF6C6C] rounded-full"
                    : "hover:ring-2 hover:ring-gray-200 rounded-full"
                }`}
              >
                <img
                  src={user.avatar || DEFAULT_AVATAR}
                  alt={user.brand_name}
                  className="w-9 h-9 rounded-full"
                />
              </button>
              <AnimatePresence>
                {accountOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="fixed left-4 right-4 top-[72px] bg-white border rounded-lg shadow-lg md:absolute md:right-0 md:w-72 md:left-auto md:top-auto md:mt-2"
                  >
                    <div className="p-4 bg-gradient-to-r from-[#FF6C6C] to-pink-500">
                      <div className="flex gap-3 items-center">
                        <img
                          src={user.avatar || DEFAULT_AVATAR}
                          alt={user.brand_name}
                          className="w-9 h-9 rounded-full border-2 border-white"
                        />
                        <div className="text-white">
                          <h3 className="font-semibold">{user.brand_name}</h3>
                          <div className="flex gap-1 items-center text-xs">
                            <Mail size={12} />
                            <span>{user.email}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Menu Items */}
                    <ul className="py-2">
                      <li>
                        <Link
                          to="/profile-settings"
                          className="flex w-full items-center gap-2 px-4 py-2 text-gray-800 hover:bg-[#FF6C6C] hover:text-white"
                          onClick={handleProfileClick}
                        >
                          <CircleUser size={20} />
                          <span>My Profile</span>
                        </Link>
                      </li>
                      <li>
                        <button
                          onClick={handleLogout}
                          className="flex w-full items-center gap-2 px-4 py-2 text-gray-800 hover:bg-[#FF6C6C] hover:text-white"
                        >
                          <LogOut size={20} />
                          <span>Logout</span>
                        </button>
                      </li>
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {mobileSidebarOpen && (
          <MobileSidebar
            isOpen={mobileSidebarOpen}
            onClose={() => setMobileSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50"
        >
          <div className="w-12 h-12 border-4 border-white border-t-[#FF6C6C] rounded-full animate-spin"></div>
        </motion.div>
      )}
    </>
  );
};

export default Navbar;
